(ns client-portal.client.voice-commerce.orders.data
  (:require [shared.core.reactive :as re]
            [shared.core.time :as time]
            [shared.core.util :as util]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.router :as cp.router]))

(def root-path [:voice-commerce :orders])
(def list-path (conj root-path :list))
(def count-path (conj root-path :count))
(def header-height-path (conj root-path :header-height))
(def selected-order-path (conj root-path :selected-order))
(def page-size 30)

;; The page header is sticky at the top of the scroll container, so the table's own
;; sticky header has to start below it rather than at 0 — measured, since the height
;; moves with the content and the viewport.
(re/reg-sub
  :voice-commerce/orders-header-height-
  (fn [db]
    (get-in db header-height-path)))

(re/reg-event-db
  :voice-commerce/orders-set-header-height re/middleware
  (fn [db [h]]
    (assoc-in db header-height-path h)))

(defn build-orders-list-page-path [page]
  (conj list-path page))

(defn current-max-page [db]
  (some->> (get-in db list-path)
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn orders-list-data-has-more? [db]
  (let [list-data (get-in db list-path)
        max-page (current-max-page db)]
    (some->> (get (util/when-not-loading list-data) max-page)
             (util/when-not-loading)
             (count)
             (<= page-size))))

(re/reg-sub :voice-commerce/orders-list-data-has-more?- orders-list-data-has-more?)

(defn orders-list-loading? [db]
  (let [list-data (get-in db list-path)]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub :voice-commerce/orders-list-loading?- orders-list-loading?)

(defn orders-list-next-page-loading? [db]
  (let [page (current-max-page db)]
    (and (pos? page) (util/loading? (get-in db (build-orders-list-page-path page))))))

(re/reg-sub :voice-commerce/orders-list-next-page-loading?- orders-list-next-page-loading?)

(defn list-next-page [db]
  (some-> (current-max-page db) (inc)))

(re/reg-sub :voice-commerce/orders-list-next-page- list-next-page)

(defn transf-order
  "The raw timestamp is kept as the paging cursor — the formatted one can't be handed
   back to the query."
  [order]
  (assoc order
    :occurred-at-raw (:occurred-at order)
    :occurred-at (time/reformat-date-str (:occurred-at order) "M/d/yy h:mm A")))

(defn transf-orders-resp [resp]
  (or (some->> resp :body
               (util/->kebab-case-keywords)
               (mapv transf-order))
      []))

(defn base-query-args [db args]
  (let [concept-id (concepts.d/current-concept-id db)
        location-id (locations.d/current-selected-location-id db)]
    (cond-> (merge (core.d/timeframe-date-params db) args)
            concept-id (assoc :concept-id concept-id)
            location-id (assoc :location-id location-id))))

(defn order-by-id-path [{:keys [order-id]}]
  (conj root-path :order-by-id order-id))

(re/reg-auth0-loader
  :voice-commerce/orders-load-list-data
  (fn [[[_ {:keys [page]}]] _]
    (build-orders-list-page-path (or page 0)))
  (fn [db [_ {:keys [page]} _]]
    (when (and (not (orders-list-next-page-loading? db))
               (not (orders-list-loading? db)))
      (when (concepts.d/current-concept-id db)
        ;; Keyset paging, as on the sessions list: the next page ends where the last
        ;; loaded row did, so rows can't shift or repeat between requests. The cursor
        ;; rides alongside the header dates rather than replacing `:end-date`, so the
        ;; server dedups every page over the same window.
        (let [last-loaded-at (some-> (util/when-not-loading (get-in db list-path))
                                     (get (or (dec (or page 0)) 0))
                                     (last)
                                     :occurred-at-raw)]
          {:method :post
           :url "/api/voice-commerce/orders/list"
           :json-params (base-query-args db (cond-> {:limit page-size}
                                                    last-loaded-at
                                                    (assoc :before last-loaded-at)))}))))
  :transf transf-orders-resp
  :error-transf (fn [] [])
  ;; Each row is also filed under its own id, so an order opened from the list is
  ;; still in hand after the list is dropped and reloaded rather than fetched again.
  :on-success (fn [db resp]
                (->> (transf-orders-resp resp)
                     (reduce (fn [db order]
                               (assoc-in db (order-by-id-path order) order))
                             db))))

(defn list->flattened-list [list-data]
  (some->> (util/when-not-loading list-data)
           (into (sorted-map))
           (filterv #(not (util/loading? (last %))))
           (mapv last)
           (flatten)
           (vec)))

(defn db->flattened-list [db]
  (-> (get-in db list-path)
      (list->flattened-list)))

;; What the detail panel renders when the order was never in a loaded list — a
;; searched or shared id still has to resolve without it.
(re/reg-auth0-loader
  :voice-commerce/order-by-id
  (fn [[[_ args]]]
    (order-by-id-path args))
  (fn [db [_ args]]
    (when-let [order-id (:order-id args)]
      {:method :get
       :url "/api/voice-commerce/orders/order"
       :query-params {:order-id order-id
                      :concept-id (concepts.d/current-concept-id db)}}))
  :transf (fn [resp]
            (some-> resp :body (util/->kebab-case-keywords) (transf-order)))
  :error-transf (fn [] {}))

(defn list-order-by-id [db order-id]
  (->> (db->flattened-list db)
       (some #(when (= (:order-id %) order-id) %))))

;; The list rows are selected exactly as the single-order lookup is, so an order that
;; is already loaded is read straight off the list; only one that isn't is fetched.
(re/reg-sub
  :voice-commerce/order-by-id-
  (fn [db [_ {:keys [order-id] :as args}]]
    (or (list-order-by-id db order-id)
        (let [v (get-in db (order-by-id-path args))]
          (if (nil? v)
            (re/dispatch-load [:voice-commerce/order-by-id [nil args]])
            v)))))

;; The window's total, so the header count doesn't grow as pages load.
(re/reg-auth0-loader
  :voice-commerce/orders-load-count
  count-path
  (fn [db]
    (when (concepts.d/current-concept-id db)
      {:method :post
       :url "/api/voice-commerce/orders/count"
       :json-params (base-query-args db {})}))
  :transf #(-> % :body (util/->kebab-case-keywords) :count)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :voice-commerce/orders-count-
  count-path
  :voice-commerce/orders-load-count)

(re/reg-sub
  :voice-commerce/orders-list-
  (fn [db]
    (let [current-list (get-in db list-path)
          cur-page (if (util/loading? current-list) 0 (current-max-page db))]
      (if (nil? current-list)
        (re/dispatch-load (conj [:voice-commerce/orders-load-list-data
                                 [nil {:page (or (some-> cur-page (inc)) 0)}]]))
        current-list))))

(re/reg-sub
  :voice-commerce/orders-list-flattened-
  :<- [:voice-commerce/orders-list-]
  list->flattened-list)

(re/reg-event-db
  :voice-commerce/orders-reload-list re/middleware
  (fn [db]
    (-> db
        (assoc-in list-path nil)
        (assoc-in count-path nil))))

;; The open order rides in the URL (base64 JSON, as the sessions drawer does with
;; `conv`), so a detail panel survives a refresh or a shared link.
(defn order->order-qp [{:keys [order-id]}]
  (try
    (some-> (when order-id
              {:order-id order-id})
            (util/remove-nil-from-map)
            (util/map->json-string)
            (util/base-64-encode))
    (catch :default _
      nil)))

(defn order-qp->order [order-qp]
  (try
    (some-> order-qp
            (util/base-64-decode)
            (util/json-string->map))
    (catch :default _
      nil)))

(re/reg-sub
  :voice-commerce/selected-order-
  (fn [db]
    (get-in db selected-order-path nil)))

(defn go-with-merged-order [order]
  (re/dispatch [:router/go-with-merged-query-params {:order (order->order-qp order)}]))

(re/reg-event-db
  :voice-commerce/orders-toggle-drawer-open re/middleware
  (fn [db [args]]
    (let [cur-order (get-in db selected-order-path)
          ;; The panel is stored as `[:voice-commerce/order-panel args]`.
          currently-open? (some-> (get-in db [:layout :active-panel-right])
                                  (first)
                                  (= :voice-commerce/order-panel))
          ;; A toggle closes only the order already open; any other row switches to it.
          open? (if (nil? (:open? args))
                  (not (and currently-open?
                            (= (:order-id cur-order) (:order-id args))))
                  (:open? args))
          args (dissoc args :open?)
          ;; Reopening from the URL only knows the order id — keep the index the row
          ;; click established, or the prev/next arrows would vanish on the round trip.
          args (if (and (= (:order-id cur-order) (:order-id args))
                        (:order-idx cur-order)
                        (not (:order-idx args)))
                 (assoc args :order-idx (:order-idx cur-order))
                 args)]
      (if open?
        (when-not currently-open?
          (re/dispatch [:layout/show-panel-right [:voice-commerce/order-panel args]]))
        (re/dispatch [:layout/hide-panel-right]))
      ;; Closing drops `order` from the URL, or the root would reopen it from there.
      (go-with-merged-order (when open? args))
      (assoc-in db selected-order-path (when open? args)))))

;; The URL no longer names an order (back button, or returning to the page) — drop
;; the selection without routing, since the URL is already where it should be.
(re/reg-event-db
  :voice-commerce/orders-clear-selected re/middleware
  (fn [db]
    (if (get-in db selected-order-path)
      (-> db
          (assoc-in selected-order-path nil)
          (cond-> (= (some-> (get-in db [:layout :active-panel-right]) (first)) :voice-commerce/order-panel)
                  (assoc-in [:layout :active-panel-right] nil)))
      db)))

(defn sharable-order-url [concept-id order]
  (str js/window.location.origin
       (cp.router/route-details->href :vc/orders
                                      nil
                                      {:cid concept-id
                                       :order (order->order-qp order)})))

;; Step to the neighbouring order in the loaded list, loading the next page when the
;; walk reaches the end of what's loaded (as `:sessions/move-conversation` does).
(re/reg-event-db
  :voice-commerce/orders-move-order re/middleware
  (fn [db [direction]]
    (let [selected-order (get-in db selected-order-path)
          orders (db->flattened-list db)
          current-index (:order-idx selected-order)
          first-order? (= current-index 0)
          last-order? (>= current-index (dec (count orders)))
          next-index (case direction
                       :next (when-not last-order? (inc current-index))
                       :prev (when-not first-order? (dec current-index)))
          next-order (get orders next-index)]
      (when (and (>= next-index (dec (count orders))) (= direction :next))
        (re/dispatch [:voice-commerce/orders-load-list-data [nil {:page (list-next-page db)}]]))
      (if next-order
        (let [order {:order-id (:order-id next-order)}]
          (go-with-merged-order order)
          (assoc-in db selected-order-path (assoc order :order-idx next-index)))
        db))))

;; Every loaded page was fetched for one concept/location/date range, so a header
;; change has to drop them all rather than append a page from a different query.
(re/reg-event-fx
  :voice-commerce/orders-initialize re/middleware
  (fn [{:keys [db]}]
    (re/dispatch [:voice-commerce/orders-reload-list])
    {:db db
     :forward-events {:register :voice-commerce-orders-initialization-listener
                      :events #{:concepts/select :locations/select :locations/clear-selected
                                :core/select-timeframe :core/timeframe-date-range-changed}
                      :dispatch-to [:voice-commerce/orders-reload-list]}}))
